import React from 'react';
import { Box, Typography, Link, Rating, Paper } from '@mui/material';
import { Star } from '@mui/icons-material';

interface GoogleReviewBadgeProps {
  variant?: 'compact' | 'full';
}

/**
 * Google Review Badge Component
 * Displays Google 5-star rating with link to reviews
 * 
 * Usage:
 * <GoogleReviewBadge variant="compact" /> - Small badge for page footer
 * <GoogleReviewBadge variant="full" /> - Full card with more details
 */
const GoogleReviewBadge: React.FC<GoogleReviewBadgeProps> = ({ variant = 'compact' }) => {
  // Your Google review link
  const GOOGLE_REVIEW_URL = 'https://share.google/sx5pq1K2STtmfHe5T';
  
  // You can update these values based on your actual Google Business stats
  const RATING = 5.0;
  const REVIEW_COUNT = 66; // Update this with your actual review count

  if (variant === 'compact') {
    return (
      <Link
        href={GOOGLE_REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          padding: '8px 16px',
          borderRadius: '8px',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            transform: 'translateY(-2px)',
            boxShadow: 2,
          },
        }}
      >
        <Box
          component="img"
          src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
          alt="Google"
          sx={{ height: 20, width: 'auto' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Rating
            value={RATING}
            precision={0.1}
            size="small"
            readOnly
            sx={{
              '& .MuiRating-iconFilled': {
                color: '#FBBC04',
              },
            }}
          />
          {/* <Typography variant="body2" sx={{ fontWeight: 600, color: '#202124' }}>
            {RATING}
          </Typography> */}
        </Box>
        <Typography variant="caption" sx={{ color: '#5f6368' }}>
          ({REVIEW_COUNT} reviews)
        </Typography>
      </Link>
    );
  }

  // Full variant
  return (
    <Paper
      elevation={2}
      sx={{
        padding: 3,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0',
      }}
    >
      <Box
        component="img"
        src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
        alt="Google"
        sx={{ height: 30, mb: 2 }}
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
        <Rating
          value={RATING}
          precision={0.1}
          size="large"
          readOnly
          sx={{
            '& .MuiRating-iconFilled': {
              color: '#FBBC04',
            },
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
          {RATING}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Based on {REVIEW_COUNT} Google reviews
      </Typography>

      <Link
        href={GOOGLE_REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 2,
          padding: '8px 16px',
          borderRadius: '20px',
          backgroundColor: '#4285F4',
          color: 'white',
          textDecoration: 'none',
          fontSize: '14px',
          fontWeight: 600,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: '#3367D6',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Star fontSize="small" />
        Read Reviews
      </Link>

      <Typography variant="caption" display="block" sx={{ mt: 2, color: '#5f6368' }}>
        Trusted by our customers
      </Typography>
    </Paper>
  );
};

export default GoogleReviewBadge;
